/**
 * Emisión fiscal síncrona para imprimir el comprobante en el momento del cobro.
 * Dispara `arca-invoice` y hace poll a `pos.invoices` hasta que haya CAE aprobado
 * (la EF puede responder 202 pending_retry mientras el worker termina).
 */
import {
  fetchFiscalInvoiceForSale,
  retryFiscalInvoiceEmit,
  type FiscalInvoiceDetailRow,
  type FiscalInvoiceEmitResult,
} from '../infra/fiscalRepository';

const DEFAULT_AWAIT_TIMEOUT_MS = 28_000;
const POLL_INTERVAL_MS = 1_200;

export type FiscalEmitAwaitOutcome = {
  emission: FiscalInvoiceEmitResult | null;
  lastError: string | null;
  invoiceStatus: string | null;
};

export function fiscalInvoiceDetailToEmitResult(
  row: FiscalInvoiceDetailRow,
): FiscalInvoiceEmitResult {
  return {
    cae: row.cae ?? undefined,
    caeExpiration: row.caeExpiration,
    status: row.status ?? undefined,
    issuedAt: row.issuedAt ?? undefined,
    invoiceType: row.invoiceType ?? undefined,
    pointOfSale: row.pointOfSale ?? undefined,
    invoiceNumber: row.invoiceNumber ?? undefined,
    netAmount: row.netAmount ?? undefined,
    ivaAmount: row.ivaAmount ?? undefined,
    totalAmount: row.totalAmount ?? undefined,
    buyerName: row.buyerName,
    buyerCuit: row.buyerCuit,
    buyerTaxCondition: row.buyerTaxCondition,
  };
}

async function invoiceSnapshotFromDb(saleId: string): Promise<FiscalInvoiceDetailRow | null> {
  return fetchFiscalInvoiceForSale(saleId);
}

async function approvedEmitFromDb(saleId: string): Promise<FiscalInvoiceEmitResult | null> {
  const inv = await invoiceSnapshotFromDb(saleId);
  if (!inv?.cae?.trim() || inv.status !== 'approved') return null;
  return fiscalInvoiceDetailToEmitResult(inv);
}

function emitResultReady(result: FiscalInvoiceEmitResult | null | undefined): result is FiscalInvoiceEmitResult {
  return Boolean(result?.cae?.trim());
}

function outcomeFromInvoice(inv: FiscalInvoiceDetailRow | null): FiscalEmitAwaitOutcome {
  if (inv?.cae?.trim() && inv.status === 'approved') {
    return {
      emission: fiscalInvoiceDetailToEmitResult(inv),
      lastError: null,
      invoiceStatus: 'approved',
    };
  }
  return {
    emission: null,
    lastError: inv?.lastError ?? null,
    invoiceStatus: inv?.status ?? null,
  };
}

export async function emitFiscalInvoiceAwaitingCae(
  saleId: string,
  timeoutMs = DEFAULT_AWAIT_TIMEOUT_MS,
): Promise<FiscalEmitAwaitOutcome> {
  const deadline = Date.now() + timeoutMs;

  try {
    const direct = await retryFiscalInvoiceEmit(saleId);
    if (emitResultReady(direct)) {
      return { emission: direct, lastError: null, invoiceStatus: 'approved' };
    }
  } catch (err) {
    console.warn('[fiscal] emit kick failed', err);
  }

  let fromDb = await approvedEmitFromDb(saleId);
  if (fromDb) {
    return { emission: fromDb, lastError: null, invoiceStatus: 'approved' };
  }

  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    fromDb = await approvedEmitFromDb(saleId);
    if (fromDb) {
      return { emission: fromDb, lastError: null, invoiceStatus: 'approved' };
    }
  }

  console.warn('[fiscal] emit await timed out — CAE no listo para imprimir');
  const inv = await invoiceSnapshotFromDb(saleId);
  return outcomeFromInvoice(inv);
}
